import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
import { DialogModule } from 'primeng/dialog';
import { BadgeModule } from 'primeng/badge';
import { trigger, transition, style, animate } from '@angular/animations';
import { UserService } from '../../layout/service/user.service';

@Component({
    selector: 'app-video-material-page',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        CardModule,
        ToastModule,
        ConfirmDialogModule,
        TooltipModule,
        ProgressSpinnerModule,
        TabsModule,
        DialogModule,
        BadgeModule
    ],
    providers: [MessageService, ConfirmationService],
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateX(-20px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
            ])
        ])
    ],
    styles: [`
        @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
        }
        .animate-shimmer {
            animation: shimmer 1.5s infinite;
        }
    `],
    template: `
        <div class="min-h-screen bg-slate-50/70 p-4 sm:p-6 lg:p-8">
            <div class="max-w-7xl mx-auto space-y-6">
                <!-- Header Banner -->
                <div class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-7 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 class="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Video Material</h1>
                        <p class="text-slate-500 text-sm mt-1">Kelola konten video untuk pembelajaran OSCE</p>
                    </div>
                    
                    <!-- Header Stat Badge -->
                    <div class="flex items-center gap-3.5 bg-blue-50/80 border border-blue-100 rounded-xl px-5 py-3.5 self-start md:self-center">
                        <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0">
                            <i class="pi pi-video text-lg"></i>
                        </div>
                        <div>
                            <div class="text-2xl font-bold text-blue-950 leading-none mb-1">{{ totalVideos }}</div>
                            <div class="text-xs text-blue-700 font-medium">Total Video Material</div>
                        </div>
                    </div>
                </div>

                <!-- Optimization Warning (Admin Only - Commented out) -->
                <!-- <div *ngIf="isAuthorizedUser && videosNeedOptimization > 0" class="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4 mb-6">
                    <div class="flex items-center gap-3">
                        <i class="pi pi-exclamation-triangle text-xl text-orange-600"></i>
                        <div>
                            <span class="font-semibold text-orange-900">{{ videosNeedOptimization }} video perlu dioptimasi</span>
                            <span class="text-sm text-orange-700 ml-2">• Proses optimasi meningkatkan kecepatan streaming</span>
                        </div>
                    </div>
                </div> -->

                <!-- Process All Videos (Admin Only - Commented out) -->
                <!-- <div *ngIf="isAuthorizedUser" class="bg-white rounded-xl shadow-lg border-2 p-8 mb-6 hover:shadow-xl transition-shadow">
                    ...
                </div> -->

                <!-- Monitoring Section (Admin Only - Commented out) -->
                <!-- <div *ngIf="isAuthorizedUser" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    ...
                </div> -->

                <!-- Quick Action Cards Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Tambah Video Card (Admin Only) -->
                    <div *ngIf="isAuthorizedUser" 
                         (click)="goToCreate()"
                         class="group relative bg-white rounded-2xl p-6 sm:p-7 border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-indigo-300 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between">
                        <div class="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full blur-2xl group-hover:bg-indigo-100 transition-all -z-0"></div>
                        
                        <div class="relative z-10">
                            <div class="flex items-center justify-between mb-5">
                                <div class="w-14 h-14 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/25 group-hover:scale-110 transition-transform duration-300">
                                    <i class="pi pi-plus text-2xl"></i>
                                </div>
                                <span class="text-xs font-semibold px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-100">
                                    Aksi Cepat
                                </span>
                            </div>
                            <h3 class="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors mb-2">
                                Tambah Video Baru
                            </h3>
                            <p class="text-slate-500 text-sm mb-6 leading-relaxed">
                                Upload dan tambahkan materi video pembelajaran OSCE baru untuk mahasiswa.
                            </p>
                        </div>
                        
                        <div class="relative z-10 flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform duration-300">
                            <span>Tambah Video</span>
                            <i class="pi pi-arrow-right ml-2 text-xs"></i>
                        </div>
                    </div>

                    <!-- Kelola Video Card -->
                    <div (click)="goToList()"
                         class="group relative bg-white rounded-2xl p-6 sm:p-7 border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-emerald-300 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between"
                         [ngClass]="!isAuthorizedUser ? 'md:col-span-2' : ''">
                        <div class="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full blur-2xl group-hover:bg-emerald-100 transition-all -z-0"></div>
                        
                        <div class="relative z-10">
                            <div class="flex items-center justify-between mb-5">
                                <div class="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300">
                                    <i class="pi pi-list text-2xl"></i>
                                </div>
                                <span class="text-xs font-semibold px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">
                                    Koleksi Video
                                </span>
                            </div>
                            <h3 class="text-xl font-bold text-slate-900 group-hover:text-emerald-600 transition-colors mb-2">
                                Kelola Video Material
                            </h3>
                            <p class="text-slate-500 text-sm mb-6 leading-relaxed">
                                Lihat, cari, saring, dan perbarui seluruh daftar video yang telah diunggah.
                            </p>
                        </div>
                        
                        <div class="relative z-10 flex items-center text-emerald-600 font-semibold text-sm group-hover:translate-x-1 transition-transform duration-300">
                            <span>Lihat Semua Video</span>
                            <i class="pi pi-arrow-right ml-2 text-xs"></i>
                        </div>
                    </div>
                </div>

                <!-- Recent Videos Section -->
                <div class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-7">
                    <div class="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                                <i class="pi pi-history text-lg"></i>
                            </div>
                            <div>
                                <h3 class="text-lg font-bold text-slate-900">Video Terbaru</h3>
                                <p class="text-slate-500 text-xs">Daftar video material yang baru diunggah</p>
                            </div>
                        </div>
                        
                        <p-button
                            (onClick)="goToList()"
                            label="Lihat Semua"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            severity="secondary"
                            [text]="true"
                            size="small">
                        </p-button>
                    </div>

                    <div *ngIf="recentVideos.length > 0" class="space-y-3">
                        <div *ngFor="let video of recentVideos"
                             class="group flex items-center justify-between p-4 border border-slate-100 hover:border-indigo-200 rounded-xl hover:bg-indigo-50/30 transition-all duration-200 cursor-pointer"
                             (click)="viewVideo(video)">
                            <div class="flex items-center gap-4 min-w-0 flex-1">
                                <div class="w-12 h-12 bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white rounded-xl flex items-center justify-center text-indigo-600 shrink-0 transition-all duration-300 shadow-sm">
                                    <i class="pi pi-video text-xl"></i>
                                </div>
                                <div class="min-w-0 flex-1 pr-4">
                                    <h4 class="font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors truncate text-base mb-1">
                                        {{ video.name }}
                                    </h4>
                                    <div class="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200/60">
                                            {{ video.kategori || 'Umum' }}
                                        </span>
                                        <span class="text-slate-300">•</span>
                                        <span class="flex items-center gap-1 text-slate-500">
                                            <i class="pi pi-calendar text-[10px]"></i>
                                            {{ formatDate(video.created_at) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class="flex items-center gap-2 shrink-0">
                                <p-button
                                    *ngIf="isAuthorizedUser"
                                    (onClick)="$event.stopPropagation(); editVideo(video)"
                                    severity="secondary"
                                    icon="pi pi-pencil"
                                    size="small"
                                    [text]="true"
                                    pTooltip="Edit Video">
                                </p-button>
                                <div class="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 group-hover:text-indigo-600 group-hover:bg-white transition-all">
                                    <i class="pi pi-chevron-right text-sm"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="recentVideos.length === 0" class="text-center py-12 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                        <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mx-auto mb-3">
                            <i class="pi pi-video text-3xl"></i>
                        </div>
                        <h4 class="font-semibold text-slate-700 mb-1">Belum Ada Video Material</h4>
                        <p class="text-slate-500 text-sm max-w-sm mx-auto mb-4">Mulai tambahkan video materi pembelajaran OSCE untuk pengguna.</p>
                        <p-button
                            *ngIf="isAuthorizedUser"
                            (onClick)="goToCreate()"
                            label="Tambah Video"
                            icon="pi pi-plus"
                            severity="primary"
                            size="small">
                        </p-button>
                    </div>
                </div>
            </div>
        </div>

        <p-confirmDialog></p-confirmDialog>
        <p-toast></p-toast>

        <!-- Logs Dialog -->
        <p-dialog 
            [(visible)]="showLogsDialog" 
            [modal]="true" 
            [style]="{width: '90vw', height: '85vh'}" 
            [draggable]="false" 
            [resizable]="false"
            [closable]="true"
            (onHide)="stopLogsPolling()">
            <ng-template pTemplate="header">
                <div class="flex items-center justify-between w-full pr-8">
                    <div class="flex items-center gap-3">
                        <i class="pi pi-terminal text-2xl text-blue-600"></i>
                        <div>
                            <h3 class="text-xl font-bold text-gray-900">Real-time Processing Logs</h3>
                            <div class="flex items-center gap-2 mt-1">
                                <i class="pi pi-circle-fill text-xs" [ngClass]="isProcessing ? 'text-green-500 animate-pulse' : 'text-gray-400'"></i>
                                <span class="text-xs" [ngClass]="isProcessing ? 'text-green-600 font-semibold' : 'text-gray-500'">
                                    {{ isProcessing ? 'Processing Active' : 'Idle' }}
                                </span>
                                <span *ngIf="loadingLogs" class="text-xs text-blue-600 flex items-center gap-1 ml-2">
                                    <i class="pi pi-spin pi-spinner text-xs"></i>
                                    <span class="animate-pulse">Syncing...</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </ng-template>
            
            <div class="h-full flex flex-col" style="height: calc(85vh - 140px);">
                <!-- Stats Bar -->
                <div class="grid grid-cols-4 gap-3 mb-4 p-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200 relative overflow-hidden">
                    <!-- Loading Shimmer Effect -->
                    <div *ngIf="loadingLogs" class="absolute inset-0 bg-gradient-to-r from-transparent via-blue-100/30 to-transparent animate-shimmer" 
                         style="animation: shimmer 1.5s infinite; background-size: 200% 100%;"></div>
                    <div class="text-center">
                        <div class="text-xs text-gray-500 mb-1">Total</div>
                        <div class="text-lg font-bold text-gray-700">{{ logs.length }}</div>
                    </div>
                    <div class="text-center">
                        <div class="text-xs text-yellow-600 mb-1 flex items-center justify-center gap-1">
                            <span>⏭</span> Skipped
                        </div>
                        <div class="text-lg font-bold text-yellow-600">{{ getLogsByLevel('warn').length }}</div>
                    </div>
                    <div class="text-center">
                        <div class="text-xs text-blue-600 mb-1 flex items-center justify-center gap-1">
                            <span>⚙️</span> Processing
                        </div>
                        <div class="text-lg font-bold text-blue-600">{{ getLogsByLevel('info').length }}</div>
                    </div>
                    <div class="text-center">
                        <div class="text-xs text-green-600 mb-1 flex items-center justify-center gap-1">
                            <span>✅</span> Success
                        </div>
                        <div class="text-lg font-bold text-green-600">{{ getLogsByLevel('success').length }}</div>
                    </div>
                </div>

                <!-- Controls Bar -->
                <div class="flex justify-between items-center mb-3 gap-3">
                    <div class="flex gap-2 flex-wrap">
                        <p-button
                            [label]="'All (' + logs.length + ')'"
                            [severity]="logFilter === 'all' ? 'primary' : 'secondary'"
                            [outlined]="logFilter !== 'all'"
                            size="small"
                            (onClick)="logFilter = 'all'">
                        </p-button>
                        <p-button
                            [label]="'Skip (' + getLogsByLevel('warn').length + ')'"
                            [severity]="logFilter === 'skip' ? 'warning' : 'secondary'"
                            [outlined]="logFilter !== 'skip'"
                            size="small"
                            (onClick)="logFilter = 'skip'">
                        </p-button>
                        <p-button
                            [label]="'Process (' + getLogsByLevel('info').length + ')'"
                            [severity]="logFilter === 'process' ? 'info' : 'secondary'"
                            [outlined]="logFilter !== 'process'"
                            size="small"
                            (onClick)="logFilter = 'process'">
                        </p-button>
                        <p-button
                            [label]="'Success (' + getLogsByLevel('success').length + ')'"
                            [severity]="logFilter === 'success' ? 'success' : 'secondary'"
                            [outlined]="logFilter !== 'success'"
                            size="small"
                            (onClick)="logFilter = 'success'">
                        </p-button>
                    </div>
                    
                    <div class="flex gap-2">
                        <p-button
                            (onClick)="toggleAutoScroll()"
                            [icon]="autoScroll ? 'pi pi-lock' : 'pi pi-lock-open'"
                            [severity]="autoScroll ? 'success' : 'secondary'"
                            [outlined]="!autoScroll"
                            size="small"
                            pTooltip="{{ autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF' }}">
                        </p-button>
                        <p-button
                            (onClick)="exportLogs()"
                            icon="pi pi-download"
                            severity="secondary"
                            [outlined]="true"
                            size="small"
                            pTooltip="Export Logs">
                        </p-button>
                        <p-button
                            (onClick)="clearLogs()"
                            icon="pi pi-trash"
                            severity="danger"
                            [outlined]="true"
                            size="small"
                            pTooltip="Clear Logs">
                        </p-button>
                        <p-button
                            (onClick)="loadLogs()"
                            icon="pi pi-refresh"
                            severity="info"
                            [outlined]="true"
                            size="small"
                            label="Refresh">
                        </p-button>
                    </div>
                </div>

                <!-- Logs Display -->
                <div #logsContainer class="flex-1 overflow-y-auto bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-4 rounded-lg shadow-inner border border-gray-700" style="max-height: calc(85vh - 350px);">
                    <div *ngIf="filteredLogs.length === 0" class="text-center py-12">
                        <i class="pi pi-inbox text-5xl text-gray-600 mb-3 block"></i>
                        <p class="text-gray-400 text-lg">{{ logs.length === 0 ? 'No logs available' : 'No logs match the filter' }}</p>
                        <p class="text-gray-500 text-sm mt-2">{{ logs.length === 0 ? 'Start processing to see logs' : 'Try a different filter' }}</p>
                    </div>
                    
                    <div *ngFor="let log of filteredLogs; let i = index" 
                         class="mb-2 p-2 rounded hover:bg-gray-800/50 transition-all duration-200 border-l-4"
                         [ngClass]="getLogBorderClass(log.level)"
                         [@slideIn]>
                        <div class="flex items-start gap-3">
                            <span class="text-xs text-gray-500 font-mono shrink-0 mt-0.5">{{ formatLogTime(log.timestamp) }}</span>
                            <div class="flex items-center gap-2 shrink-0">
                                <span class="text-lg">{{ getLogIcon(log.level) }}</span>
                                <p-badge 
                                    [value]="getLogLevelLabel(log.level)" 
                                    [severity]="getLogBadgeSeverity(log.level)"
                                    styleClass="text-xs">
                                </p-badge>
                            </div>
                            <span class="font-mono text-sm flex-1" [ngClass]="getLogClass(log.level)">{{ log.message }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </p-dialog>
    `
})
export class VideoMaterialPage implements OnInit, OnDestroy {
    totalVideos = 0;
    recentVideos: any[] = [];
    isAuthorizedUser = false;
    isProcessing = false;
    videosNeedOptimization = 0; // Count of videos with is_faststart == 0
    
    // Monitoring properties
    stats: any = null;
    health: any = null;
    logs: any[] = [];
    loadingStats = false;
    loadingHealth = false;
    loadingLogs = false;
    showLogsDialog = false;
    private logsPollingInterval: any = null;
    
    // Logs filtering and stats
    logFilter: string = 'all'; // all, skip, process, success, error
    autoScroll: boolean = true;

    constructor(
        private router: Router,
        private userService: UserService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {}

    ngOnInit() {
        this.checkUserAuthorization();
        this.loadDashboardData();
    }

    ngOnDestroy() {
        this.stopLogsPolling();
    }

    checkUserAuthorization() {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                this.isAuthorizedUser = user.ID_ACCESS_ROLE === 'N2FWBVCZUE' || user.ID_ACCESS_ROLE === '4AEJ1KAJTY';
            }
        } catch (error) {
            console.error('Error checking user authorization:', error);
            this.isAuthorizedUser = false;
        }
    }

    loadDashboardData() {
        // Load video materials for statistics and recent videos
        this.userService.getVideoMaterials().then(
            (response) => {
                const videos = response.data || response || [];
                this.totalVideos = videos.length;
                this.recentVideos = videos.slice(0, 4); // Show 4 most recent
                
                // Count videos that need optimization (is_faststart == 0 or null)
                this.videosNeedOptimization = videos.filter((video: any) => 
                    video.is_faststart === '0' || video.is_faststart === 0 || !video.is_faststart
                ).length;
            },
            (error) => {
                console.error('Error loading video materials:', error);
            }
        );
    }

    goToCreate() {
        this.router.navigate(['/app/video_material/create']);
    }

    goToList() {
        this.router.navigate(['/app/video_material/list']);
    }

    editVideo(video: any) {
        this.router.navigate(['/app/video_material/edit', video.id]);
    }

    viewVideo(video: any) {
        this.router.navigate(['/app/video_material/show', video.id]);
    }

    deleteVideo(video: any) {
        this.confirmationService.confirm({
            message: `Apakah Anda yakin ingin menghapus video "${video.name}"?`,
            header: 'Konfirmasi Hapus',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Hapus',
            rejectLabel: 'Batal',
            accept: () => {
                this.userService.deleteVideoMaterial({ id: video.id }).then(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: 'Video berhasil dihapus'
                        });
                        this.loadDashboardData(); // Refresh data
                    },
                    (error) => {
                        console.error('Error deleting video:', error);
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Gagal menghapus video'
                        });
                    }
                );
            }
        });
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    processAllVideos() {
        this.confirmationService.confirm({
            message: 'Apakah Anda yakin ingin memproses ulang semua video? Proses ini mungkin memakan waktu lama.',
            header: 'Konfirmasi Process All Videos',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Ya, Proses',
            rejectLabel: 'Batal',
            accept: () => {
                this.isProcessing = true;
                this.showLogsDialog = true; // Open logs dialog immediately
                this.messageService.add({
                    severity: 'info',
                    summary: 'Processing',
                    detail: 'Memulai pemrosesan semua video...'
                });

                // Start logs polling every 3 seconds
                this.startLogsPolling();

                this.userService.processAllVideos().then(
                    (response) => {
                        this.isProcessing = false;
                        this.stopLogsPolling();
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: response?.message || 'Semua video berhasil diproses'
                        });
                        this.loadDashboardData(); // Refresh data
                        this.loadStats(); // Refresh stats
                        this.loadLogs();
                    },
                    (error) => {
                        this.isProcessing = false;
                        this.stopLogsPolling();
                        console.error('Error processing videos:', error);
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error?.error?.message || 'Gagal memproses video'
                        });
                    }
                );
            }
        });
    }

    loadStats() {
        this.loadingStats = true;
        this.userService.getVideoProcessingStats().then(
            (response) => {
                this.stats = response.data || response;
                this.loadingStats = false;
            },
            (error) => {
                console.error('Error loading stats:', error);
                this.loadingStats = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat statistik'
                });
            }
        );
    }

    checkHealth() {
        this.loadingHealth = true;
        this.userService.checkVideoProcessingHealth().then(
            (response) => {
                this.health = response.data || response;
                // Add timestamp for last check
                this.health.timestamp = new Date().toISOString();
                this.loadingHealth = false;
            },
            (error) => {
                console.error('Error checking health:', error);
                this.loadingHealth = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memeriksa kesehatan sistem'
                });
            }
        );
    }

    isHealthy(status: string): boolean {
        // Check if status indicates healthy/ok state
        const healthyStatuses = ['ok', 'healthy', 'running', 'active', 'success'];
        return healthyStatuses.includes(status?.toLowerCase());
    }

    formatDateTime(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }

    loadLogs() {
        this.loadingLogs = true;
        this.userService.getVideoProcessingLogs().then(
            (response) => {
                const logsData = response.logs || response.data || response;
                
                if (Array.isArray(logsData)) {
                    // Parse array of log strings
                    this.logs = logsData.map((logLine: string) => {
                        // Remove trailing newline
                        const cleanLine = logLine.replace(/\n$/, '');
                        
                        // Extract timestamp and message
                        // Format: [2026-01-11 20:14:52] message
                        const timestampMatch = cleanLine.match(/^\[([^\]]+)\]\s*(.*)$/);
                        
                        if (timestampMatch) {
                            const timestamp = timestampMatch[1];
                            const message = timestampMatch[2];
                            
                            // Detect log level from message
                            let level = 'info';
                            if (message.includes('ERROR') || message.includes('❌')) {
                                level = 'error';
                            } else if (message.includes('SKIP') || message.includes('⏭')) {
                                level = 'warn';
                            } else if (message.includes('SUCCESS') || message.includes('✅')) {
                                level = 'success';
                            } else if (message.includes('PROCESS') || message.includes('⚙️')) {
                                level = 'info';
                            }
                            
                            return {
                                timestamp: timestamp,
                                message: message,
                                level: level
                            };
                        } else {
                            // Fallback for logs without timestamp
                            return {
                                timestamp: new Date().toISOString(),
                                message: cleanLine,
                                level: 'info'
                            };
                        }
                    }).filter(log => log.message.trim() !== ''); // Remove empty logs
                } else if (typeof logsData === 'string') {
                    // Parse string logs into array format
                    this.logs = logsData.split('\n')
                        .filter(line => line.trim() !== '')
                        .map((line: string) => {
                            const timestampMatch = line.match(/^\[([^\]]+)\]\s*(.*)$/);
                            if (timestampMatch) {
                                return {
                                    timestamp: timestampMatch[1],
                                    message: timestampMatch[2],
                                    level: 'info'
                                };
                            }
                            return {
                                timestamp: new Date().toISOString(),
                                message: line,
                                level: 'info'
                            };
                        });
                } else {
                    this.logs = [];
                }
                
                // Auto-scroll to bottom if enabled
                if (this.autoScroll) {
                    this.scrollToBottom();
                }
                
                // Smooth transition - delay loading state change for better UX
                setTimeout(() => {
                    this.loadingLogs = false;
                }, 400);
            },
            (error) => {
                console.error('Error loading logs:', error);
                this.loadingLogs = false;
            }
        );
    }

    startLogsPolling() {
        // Load logs immediately
        this.loadLogs();
        
        // Then poll every 3 seconds
        this.logsPollingInterval = setInterval(() => {
            if (this.isProcessing) {
                this.loadLogs();
            }
        }, 3000);
    }

    stopLogsPolling() {
        if (this.logsPollingInterval) {
            clearInterval(this.logsPollingInterval);
            this.logsPollingInterval = null;
        }
    }

    getLogClass(level: string): string {
        switch (level?.toLowerCase()) {
            case 'error':
                return 'text-red-400';
            case 'warn':
            case 'warning':
                return 'text-yellow-300';
            case 'success':
                return 'text-green-400';
            case 'info':
            default:
                return 'text-cyan-400';
        }
    }

    getLogBorderClass(level: string): string {
        switch (level?.toLowerCase()) {
            case 'error':
                return 'border-red-500';
            case 'warn':
            case 'warning':
                return 'border-yellow-500';
            case 'success':
                return 'border-green-500';
            case 'info':
            default:
                return 'border-cyan-500';
        }
    }

    getLogIcon(level: string): string {
        switch (level?.toLowerCase()) {
            case 'error':
                return '❌';
            case 'warn':
            case 'warning':
                return '⏭';
            case 'success':
                return '✅';
            case 'info':
            default:
                return '⚙️';
        }
    }

    getLogLevelLabel(level: string): string {
        switch (level?.toLowerCase()) {
            case 'error':
                return 'ERROR';
            case 'warn':
            case 'warning':
                return 'SKIP';
            case 'success':
                return 'SUCCESS';
            case 'info':
            default:
                return 'PROCESS';
        }
    }

    getLogBadgeSeverity(level: string): string {
        switch (level?.toLowerCase()) {
            case 'error':
                return 'danger';
            case 'warn':
            case 'warning':
                return 'warning';
            case 'success':
                return 'success';
            case 'info':
            default:
                return 'info';
        }
    }

    formatLogTime(timestamp: string): string {
        try {
            const date = new Date(timestamp);
            return date.toLocaleTimeString('id-ID', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
            });
        } catch {
            return timestamp;
        }
    }

    get filteredLogs(): any[] {
        if (this.logFilter === 'all') {
            return this.logs;
        }
        
        const filterMap: { [key: string]: string } = {
            'skip': 'warn',
            'process': 'info',
            'success': 'success',
            'error': 'error'
        };
        
        const targetLevel = filterMap[this.logFilter];
        return this.logs.filter(log => log.level === targetLevel);
    }

    getLogsByLevel(level: string): any[] {
        return this.logs.filter(log => log.level === level);
    }

    toggleAutoScroll(): void {
        this.autoScroll = !this.autoScroll;
        if (this.autoScroll) {
            this.scrollToBottom();
        }
    }

    scrollToBottom(): void {
        setTimeout(() => {
            const logsContainer = document.querySelector('.overflow-y-auto.bg-gradient-to-br') as HTMLElement;
            if (logsContainer) {
                logsContainer.scrollTop = logsContainer.scrollHeight;
            }
        }, 100);
    }

    exportLogs(): void {
        const logsText = this.logs.map(log => 
            `[${log.timestamp}] ${this.getLogLevelLabel(log.level)}: ${log.message}`
        ).join('\n');
        
        const blob = new Blob([logsText], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `video-processing-logs-${new Date().toISOString().split('T')[0]}.txt`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.messageService.add({
            severity: 'success',
            summary: 'Exported',
            detail: 'Logs exported successfully'
        });
    }

    clearLogs(): void {
        this.logs = [];
        this.messageService.add({
            severity: 'info',
            summary: 'Cleared',
            detail: 'Logs cleared'
        });
    }
}